// src/utils/razorpay.js

import axios from "axios";

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

// Validate environment variables at runtime (client should ONLY have the public key)
const validateRazorpayConfig = () => {
  if (!RAZORPAY_KEY_ID) {
    throw new Error('REACT_APP_RAZORPAY_KEY_ID environment variable is required');
  }
};

// Constants for fixed fees
const INSPECTION_FEE_PER_PRODUCT = 30000; // ₹30,000 per product
const REGISTRATION_FEE = 59000; // ₹59,000 (includes GST)
const GST_PERCENTAGE = 18;

// Helper function to format amount in Indian currency format
export const formatIndianCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Calculate EDCC pricing with detailed breakdown
export const calculateEDCCPricing = (products = []) => {
  // Initialize totals
  let productFeesTotal = 0;
  let inspectionFeesTotal = 0;
  const productBreakdown = [];

  // Calculate fees for each product
  products.forEach(product => {
    if (product?.heading && product?.price) {
      // Ensure numbers are properly parsed
      const productFee = Number(product.price) || 0;
      const inspectionFee = Number(product.inspection_fee || INSPECTION_FEE_PER_PRODUCT) || 0;
      
      productFeesTotal += productFee;
      inspectionFeesTotal += inspectionFee;

      productBreakdown.push({
        heading: product.heading,
        productFee,
        inspectionFee,
        total: productFee + inspectionFee
      });
    }
  });

  // Calculate base amount before GST (excluding registration fee)
  const baseAmount = productFeesTotal + inspectionFeesTotal;
  
  // Calculate GST on base amount
  const gstAmount = Math.round((baseAmount * GST_PERCENTAGE) / 100);
  
  // Calculate total (including registration fee which already has GST)
  const totalAmount = baseAmount + gstAmount + REGISTRATION_FEE;

  // Log the calculation for debugging
  console.log('EDCC Pricing Calculation:', {
    productFeesTotal,
    inspectionFeesTotal,
    baseAmount,
    gstAmount,
    registrationFee: REGISTRATION_FEE,
    totalAmount
  });

  return {
    productBreakdown,
    summary: {
      productFeesTotal,
      inspectionFeesTotal,
      baseAmount,
      gstAmount,
      registrationFee: REGISTRATION_FEE,
      totalAmount
    },
    gstPercentage: GST_PERCENTAGE
  };
};

// Load Razorpay script
export const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    // Validate Razorpay configuration
    try {
      validateRazorpayConfig();
    } catch (error) {
      reject(error);
      return;
    }
    
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.body.appendChild(script);
  });
};


export const createOrder = async ({
  baseUrl,
  token,
  amountInRupees,
  products,
  notes = {}
}) => {
  if (!baseUrl || !token || !amountInRupees) {
    throw new Error("Missing required parameters");
  }

  const amount = Math.round(Number(amountInRupees));
  if (isNaN(amount) || amount <= 0) {
    throw new Error("Invalid amount");
  }

  // Log the amount for debugging
  console.log('Order Amount Details:', {
    amountInRupees: amount,
    amountInPaise: amount * 100,
    products: products.map(p => ({
      heading: p.heading,
      price: p.price,
      inspection_fee: p.inspection_fee
    }))
  });

  try {
    const response = await axios.post(
      `${baseUrl}/createOrder`,
      {
        amountInRupees: amount,
        amountInPaise: amount * 100, // Send both for clarity
        currency: 'INR',
        receipt: `receipt_${Date.now()}`, // Add receipt ID
        notes: {
          ...notes,
          module: 'EDCC',
          products: products.map(p => ({
            heading: p.heading,
            productFee: p.price,
            inspectionFee: p.inspection_fee
          })),
          registrationFee: REGISTRATION_FEE,
          gstPercentage: GST_PERCENTAGE
        }
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Create order error:', error);
    console.error('Error details:', {
      status: error.response?.status,
      data: error.response?.data,
      amountInRupees: amount,
      requestPayload: {
        amountInRupees: amount,
        amountInPaise: amount * 100,
        currency: 'INR',
        notes: notes
      }
    });
    throw new Error(error.response?.data?.message || 'Failed to create order');
  }
};

// Open Razorpay checkout
// src/utils/razorpay.js

export const openRazorpayCheckout = async ({
  order,
  name,
  description,
  prefill,
  notes,
  onSuccess,
  onFailure,
  memberId,
  memberType,
}) => {
  try {
    validateRazorpayConfig();

    console.log('Opening Razorpay checkout with order:', order);

    const baseUrl = process.env.REACT_APP_BASEURL;
    let orderId, amount;

    if (order.order && order.order.id) {
      orderId = order.order.id;
      amount = order.order.amount;
    } else {
      orderId = order.id || order.order_id || order.data?.order_id;
      amount = order.amount || order.data?.amount;
    }

    if (!orderId || !amount) {
      console.error('Invalid order details:', { order, orderId, amount });
      throw new Error('Invalid order details');
    }

    console.log('Extracted order:', { orderId, amount });

    const options = {
      key: RAZORPAY_KEY_ID,
      amount: amount,
      currency: 'INR',
      name: name || 'AIOTA EDCC',
      description: description || 'EDCC Certification Payment',
      order_id: orderId,
      prefill: {
        name: prefill?.name || '',
        email: prefill?.email || '',
        contact: prefill?.contact || ''
      },
      notes: {
        email: prefill?.email || '',
        contact: prefill?.contact || '',
        ...notes,
        module: 'EDCC'
      },
      handler: function(response) {
        console.log('Payment success:', response);
        if (onSuccess) onSuccess(response);
      },
      modal: {
        ondismiss: function () {
          console.warn("User closed Razorpay popup");

          const failureData = {
            order_id: orderId,
            memberId,
            memberType,
            reason: "USER_CLOSED_POPUP",
            created_at: new Date().toISOString(),
          };

          // Send to backend
          fetch(`${baseUrl}/payment-failed`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(failureData),
          }).catch(() => {});

          if (onFailure) {
            onFailure(new Error('Payment cancelled by user'));
          }
        }
      },

      theme: {
        color: '#528FF0'
      }
    };

    console.log('Initializing Razorpay checkout...');
    const rzp = new window.Razorpay(options);

    // Capture real payment failures
    rzp.on("payment.failed", function (response) {
      const error = response.error;

      console.error("Razorpay Payment Failed FULL:", response);

      const failureData = {
        order_id: error.metadata?.order_id || orderId,
        payment_id: error.metadata?.payment_id || null,
        code: error.code,
        description: error.description,
        source: error.source,
        step: error.step,
        reason: error.reason,
        created_at: new Date().toISOString(),
        memberId,
        memberType,
      };

      console.log("Failure Data:", failureData);

      // Send to backend
      fetch(`${baseUrl}/payment-failed`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(failureData),
      }).catch((err) => {
        console.error("Failed to log payment failure:", err);
      });

      if (onFailure) {
        onFailure(new Error(error.description || "Payment failed"));
      }
    });

    rzp.open();

  } catch (error) {
    console.error('Error opening Razorpay checkout:', error);
    if (onFailure) onFailure(error);
  }
};

// Create order for membership registration (no auth required)
export const createMembershipOrder = async ({
  baseUrl,
  amountInRupees,
  membershipType,
  memberId,
  notes = {}
}) => {
  if (!baseUrl || !amountInRupees || !membershipType || !memberId) {
    throw new Error("Missing required parameters");
  }

  const amount = Math.round(Number(amountInRupees));
  if (isNaN(amount) || amount <= 0) {
    throw new Error("Invalid amount");
  }

  // Map membership types to backend expected values
  let backendMembershipType;
  if (membershipType === 'full' || membershipType === 'life') {
    backendMembershipType = 'lifemembership';
  } else if (membershipType === 'associate') {
    backendMembershipType = 'associatemembership';
  } else {
    backendMembershipType = membershipType;
  }

  try {
    console.log('Creating membership order with:', {
      baseUrl,
      amountInRupees: amount,
      membershipType: backendMembershipType,
      memberId,
      notes
    });

    const response = await axios.post(
      `${baseUrl}/createMembershipOrder`,
      {
        amountInRupees: amount,
        amountInPaise: amount * 100,
        currency: 'INR',
        receipt: `receipt_${Date.now()}`,
        notes: {
          ...notes,
          module: 'Membership',
          membership_type: backendMembershipType,
          member_id: memberId
        }
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Membership order created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Create membership order error:', error);
    console.error('Error details:', {
      status: error.response?.status,
      data: error.response?.data,
      amountInRupees: amount,
      membershipType: backendMembershipType,
      memberId,
      requestPayload: {
        amountInRupees: amount,
        amountInPaise: amount * 100,
        currency: 'INR',
        notes: notes
      }
    });
    throw new Error(error.response?.data?.message || 'Failed to create order');
  }
};

// Verify payment (with authentication - for EDCC)
export const verifyPayment = async ({ baseUrl, payload }) => {
  if (!baseUrl || !payload) {
    throw new Error('Missing required parameters');
  }

  try {
    const response = await axios.post(
      `${baseUrl}/verifyPayment`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Payment verification error:', error);
    throw new Error(error.response?.data?.message || 'Payment verification failed');
  }
};

// Verify membership payment (no authentication required)
export const verifyMembershipPayment = async ({ 
  baseUrl, 
  payload, 
  membershipType,
  memberId 
}) => {
  if (!baseUrl || !payload || !membershipType || !memberId) {
    throw new Error('Missing required parameters for membership payment verification');
  }

  // Map membership types to backend expected values
  let backendMembershipType;
  if (membershipType === 'full' || membershipType === 'life') {
    backendMembershipType = 'lifemembership';
  } else if (membershipType === 'associate') {
    backendMembershipType = 'associatemembership';
  } else {
    backendMembershipType = membershipType;
  }

  try {
    console.log('Verifying membership payment with:', {
      baseUrl,
      payload,
      membershipType: backendMembershipType,
      memberId
    });

    const response = await axios.post(
      `${baseUrl}/verifyMembershipPayment`,
      {
        ...payload,
        membership_type: backendMembershipType,
        member_id: memberId
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Membership payment verification response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Membership payment verification error:', error);
    console.error('Error details:', {
      status: error.response?.status,
      data: error.response?.data,
      payload,
      membershipType: backendMembershipType,
      memberId
    });
    throw new Error(error.response?.data?.message || 'Membership payment verification failed');
  }
};

// Create order for WFOT form payment (no auth required)
export const createWfotOrder = async ({
  baseUrl,
  amountInRupees,
  wfotId,
  notes = {}
}) => {
  if (!baseUrl || !amountInRupees || !wfotId) {
    throw new Error("Missing required parameters for WFOT order creation");
  }

  const amount = Math.round(Number(amountInRupees));
  if (isNaN(amount) || amount <= 0) {
    throw new Error("Invalid amount");
  }

  try {
    console.log('Creating WFOT order with:', {
      baseUrl,
      amountInRupees: amount,
      wfotId,
      notes
    });

    const response = await axios.post(
      `${baseUrl}/createWfotOrder`,
      {
        amountInRupees: amount,
        amountInPaise: amount * 100,
        currency: 'INR',
        receipt: `wfot_receipt_${Date.now()}`,
        notes: {
          ...notes,
          module: 'WFOT_FORM',
          wfot_id: wfotId
        }
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('WFOT order created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Create WFOT order error:', error);
    console.error('Error details:', {
      status: error.response?.status,
      data: error.response?.data,
      amountInRupees: amount,
      wfotId,
      requestPayload: {
        amountInRupees: amount,
        amountInPaise: amount * 100,
        currency: 'INR',
        notes: notes
      }
    });
    throw new Error(error.response?.data?.message || 'Failed to create WFOT order');
  }
};

// Verify WFOT payment (no authentication required)
export const verifyWfotPayment = async ({ 
  baseUrl, 
  payload, 
  wfotId 
}) => {
  if (!baseUrl || !payload || !wfotId) {
    throw new Error('Missing required parameters for WFOT payment verification');
  }

  try {
    console.log('Verifying WFOT payment with:', {
      baseUrl,
      payload,
      wfotId
    });

    const response = await axios.post(
      `${baseUrl}/verifyWfotPayment`,
      {
        ...payload,
        wfot_id: wfotId
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('WFOT payment verification response:', response.data);
    return response.data;
  } catch (error) {
    console.error('WFOT payment verification error:', error);
    console.error('Error details:', {
      status: error.response?.status,
      data: error.response?.data,
      payload,
      wfotId
    });
    throw new Error(error.response?.data?.message || 'WFOT payment verification failed');
  }
};

// Create order for membership renewal
export const createRenewalOrder = async ({
  baseUrl,
  membershipId,
  membershipType,
  notes = {}
}) => {
  if (!baseUrl || !membershipId || !membershipType) {
    throw new Error("Missing required parameters for renewal order");
  }

  try {
    const response = await axios.post(
      `${baseUrl}/renewal/order`,
      {
        membership_id: membershipId,
        membership_type: membershipType,
        ...notes
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Create renewal order error:', error);
    throw new Error(error.response?.data?.message || 'Failed to create renewal order');
  }
};

// Verify renewal payment
export const verifyRenewalPayment = async ({
  baseUrl,
  membershipId,
  membershipType,
  payload
}) => {
  try {
    const endpoint = membershipType === 'lifemembership' 
      ? `/life/renewal/${membershipId}/payment`
      : `/associate/renewal/${membershipId}/payment`;

    const response = await axios.post(
      `${baseUrl}${endpoint}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to verify renewal payment');
  }
};

// Get renewal status
export const getRenewalStatus = async ({
  baseUrl,
  membershipId
}) => {
  try {
    const response = await axios.get(
      `${baseUrl}/life/renewal/${membershipId}/status`
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get renewal status');
  }
};



export const checkLifeRenewalEligibility = async ({ baseUrl, membershipId }) => {
  try {
    const token = localStorage.getItem('token');

    // Try with /api prefix first (some deployments mount under /api)
    try {
      const responseWithApi = await axios.get(
        `${baseUrl}/life/renewal/${membershipId}/status`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return responseWithApi.data;
    } catch (errWithApi) {
      // Fallback to route without /api prefix (for direct deployments)
      const responseNoApi = await axios.get(
        `${baseUrl}/life/renewal/${membershipId}/status`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return responseNoApi.data;
    }
  } catch (error) {
    console.error('Life renewal eligibility check error:', error);
    throw new Error(error.response?.data?.message || 'Failed to check renewal eligibility');
  }
};

// Check renewal eligibility for associate membership
export const checkAssociateRenewalEligibility = async ({
  baseUrl,
  membershipId
}) => {
  try {
    const token = localStorage.getItem('token');

    // Try with /api prefix first (some deployments mount under /api)
    try {
      const responseWithApi = await axios.get(
        `${baseUrl}/associate-membership/check-renewal-eligibility/${membershipId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return responseWithApi.data;
    } catch (errWithApi) {
      // Fallback to route without /api prefix as per updated controller
      const responseNoApi = await axios.get(
        `${baseUrl}/associate-membership/check-renewal-eligibility/${membershipId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );
      return responseNoApi.data;
    }
  } catch (error) {
    console.error('Associate renewal eligibility check error:', error);
    throw new Error(error.response?.data?.message || 'Failed to check renewal eligibility');
  }
};

// Create renewal order using new backend endpoint
export const createRenewalOrderNew = async ({
  baseUrl,
  membershipId,
  membershipType,
  notes = {}
}) => {
  try {
    console.log('Creating renewal order with new endpoint:', {
      baseUrl,
      membershipId,
      membershipType,
      notes
    });

    const response = await axios.post(
      `${baseUrl}/razorpay/renewal/create-order`,
      {
        membership_id: membershipId,
        membership_type: membershipType,
        ...notes
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Renewal order created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Create renewal order error:', error);
    throw new Error(error.response?.data?.message || 'Failed to create renewal order');
  }
};

// Process life membership renewal payment
export const processLifeRenewalPayment = async ({
  baseUrl,
  membershipId,
  payload
}) => {
  try {
    console.log('Processing life renewal payment:', {
      baseUrl,
      membershipId,
      payload
    });

    const response = await axios.post(
      `${baseUrl}/life/renewal/${membershipId}/payment`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Life renewal payment processed:', response.data);
    return response.data;
  } catch (error) {
    console.error('Life renewal payment processing error:', error);
    throw new Error(error.response?.data?.message || 'Failed to process renewal payment');
  }
};

// Process associate membership renewal payment
export const processAssociateRenewalPayment = async ({
  baseUrl,
  membershipId,
  payload,
  
}) => {
  
  try {
    console.log('Processing associate renewal payment:', {
      baseUrl,
      membershipId,
      payload
    });

    const response = await axios.post(
      `${baseUrl}/associate/renewal/${membershipId}/payment`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Associate renewal payment processed:', response.data);
    return response.data;
  } catch (error) {
    console.error('Associate renewal payment processing error:', error);
    throw new Error(error.response?.data?.message || 'Failed to process renewal payment');
  }
};

// Reset secretary renewal approval for life membership
export const resetLifeSecretaryRenewalApproval = async ({
  baseUrl,
  memberId
}) => {
  try {
    const token = localStorage.getItem('token');
    
    console.log('Resetting life secretary renewal approval:', {
      baseUrl,
      memberId
    });

    const response = await axios.put(
      `${baseUrl}/life/reset-secretary-renewal-approval/${memberId}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      }
    );
    
    console.log('Life secretary renewal approval reset:', response.data);
    return response.data;
  } catch (error) {
    console.error('Life secretary renewal approval reset error:', error);
    throw new Error(error.response?.data?.message || 'Failed to reset secretary renewal approval');
  }
};

// Reset secretary renewal approval for associate membership
export const resetAssociateSecretaryRenewalApproval = async ({
  baseUrl,
  memberId
}) => {
  try {
    const token = localStorage.getItem('token');
    
    console.log('Resetting associate secretary renewal approval:', {
      baseUrl,
      memberId
    });

    const response = await axios.put(
      `${baseUrl}/associate/reset-secretary-renewal-approval/${memberId}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      }
    );
    
    console.log('Associate secretary renewal approval reset:', response.data);
    return response.data;
  } catch (error) {
    console.error('Associate secretary renewal approval reset error:', error);
    throw new Error(error.response?.data?.message || 'Failed to reset secretary renewal approval');
  }
};